/**
 * AuthProvider - Conditional Authentication Provider Wrapper
 *
 * Wraps the application with the appropriate authentication provider
 * based on the cached auth provider preference. Uses bridge components
 * to call provider-specific hooks safely within their required context.
 *
 * Architecture:
 * - AuthProvider detects provider type (MSAL or Kinde)
 * - Renders appropriate SDK provider (MsalProvider or KindeProvider)
 * - Bridge component inside calls provider-specific hook
 * - Bridge provides unified interface via AuthContext
 * - useAuth() consumes AuthContext
 *
 * @module AuthProvider
 * @see {@link docs/KINDE_MIGRATION_PLAN.md} for migration details
 */

import { lazy, Suspense, useEffect, useMemo, useRef } from 'react'
import { KindeProvider } from '@kinde-oss/kinde-auth-react'
import PropTypes from 'prop-types'

import TbxLoadingOverlay from '@/common/components/TbxLoadingOverlay/TbxLoadingOverlay'
import {
    AUTH_PROVIDERS,
    clearAuthProvider,
    getAuthProvider,
    getRegistrationProvider,
    setAuthProvider,
} from '@/common/helpers/auth/authProviderDetection'
import { allowsUnauthenticatedEntry } from '@/common/helpers/auth/isPublicPath'
import { purgeAuthState } from '@/common/helpers/auth/purgeAuthState'
import { useKindeManager } from '@/common/hooks/useKindeAuth'
import { Paths } from '@/common/utils'
import { kindeConfig } from '@/kindeConfig'

import { AuthContext } from './AuthContext'

/**
 * Handles Kinde SDK errors during initialization or token refresh.
 * When a user is reverted from Kinde back to B2C, their Kinde account is deleted.
 * The stale refresh token triggers an `invalid_grant` error from Kinde's token endpoint.
 * Clearing the provider cache ensures the user falls through to B2C on next load.
 *
 * @param {Object} props - Error properties from Kinde SDK
 * @param {string} props.error - OAuth error code (e.g., 'invalid_grant')
 * @param {string} props.errorDescription - Human-readable error description
 */
const handleKindeError = async ({ error, errorDescription }) => {
    if (error === 'invalid_grant') {
        console.warn('[Auth] Kinde auth failed — clearing provider cache:', error, errorDescription)
        await purgeAuthState()
        window.location.href = '/'
        return
    }
    if (error === 'ERR_LOGOUT' || error === 'ERR_POPUP') {
        console.warn('[Auth] Kinde logout failed — forcing local cleanup:', error, errorDescription)
        await purgeAuthState()
        window.location.href = Paths.SIGNOUT_PATHNAME
        return
    }
    console.warn('[Auth] Kinde error:', error, errorDescription)
}

/**
 * Sends the user back to a protected route that started its own login, once Kinde's handshake
 * completes. Kinde always returns to the configured `redirectUri` (origin root) — it will not
 * exchange a code landed anywhere else — so a deep link can only survive the round trip via the
 * OAuth `state` parameter (see `getPostLoginReturnTo` and `UserRequiredContainer`), read back
 * here.
 *
 * A full navigation is the only option: `AuthProvider` is mounted above `RouterProvider`
 * (`src/index.jsx` vs `src/App.jsx`), so there is no router `navigate` to call. No-ops for a
 * plain login (no `state`, or a `returnTo` that isn't a same-origin path — the latter would
 * otherwise be an open redirect, though today's only caller is a fixed path constant, not
 * anything Kinde or the user supplies).
 *
 * @param {Object} _user - The authenticated user profile (unused).
 * @param {{ returnTo?: string }} [state] - The state object round-tripped through the OAuth
 *   handshake by `login({ state })`.
 */
const handleKindeSuccess = (_user, state) => {
    if (typeof state?.returnTo === 'string' && state.returnTo.startsWith('/')) {
        window.location.href = state.returnTo
    }
}

/** Stable callbacks reference to avoid KindeProvider re-renders */
const kindeCallbacks = { onError: handleKindeError, onSuccess: handleKindeSuccess }

/**
 * Reads the cached auth provider and reports whether it is currently set to
 * Kinde. Used by `KindeAuthBridge` to gate behavior that should only apply
 * to users with an explicit Kinde cache (vs. fresh visitors mounted via the
 * registration-provider fallback).
 *
 * @returns {boolean} True iff `localStorage.quotemate_auth_provider === 'Kinde'`
 */
const isKindeCacheActive = () => getAuthProvider() === AUTH_PROVIDERS.KINDE

/**
 * Lazy-loaded MSAL Provider Wrapper.
 * This enables code-splitting so Kinde users never download MSAL bundles.
 */
const LazyMsalProviderWrapper = lazy(() =>
    import('./MsalProviderWrapper').then((module) => ({
        default: module.MsalProviderWrapper,
    }))
)

/**
 * Bridge component for Kinde authentication.
 * Must be rendered inside KindeProvider to safely call useKindeManager().
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @returns {JSX.Element} Children wrapped with AuthContext.Provider
 */
const KindeAuthBridge = ({ children }) => {
    const kindeManager = useKindeManager()

    // Extract values for more granular memoization
    const { isAuthenticated, isInitializing, user } = kindeManager

    // QM-3353: Persist 'Kinde' to the provider cache on successful auth.
    // Pre-QM-3353, the email-first form set this. Without that form, the cache
    // would stay null after a Kinde-only login, and getApiAccessToken would
    // fall through to MSAL, returning no token and triggering 401 → /signout.
    //
    // QM-3440: This MUST run synchronously during render, NOT in a useEffect.
    // The cache is read by getApiAccessToken/prepareAuthHeaders when
    // UserRequiredContainer (a descendant of this bridge) fires the
    // POST /users/context request from its own effect. React runs descendant
    // effects BEFORE ancestor effects, so an effect-based write here loses the
    // race on a fresh session (incognito, cleared storage, first login after
    // migration): the user-context POST goes out while the cache is still null,
    // falls through to the MSAL token path, gets no token, and 401s the user to
    // /signout. Writing during render guarantees the cache is set before any
    // descendant effect runs. The ref keeps the write to once per mount; the
    // write is idempotent and self-extinguishing (isKindeCacheActive() is true
    // afterwards), mirroring the synchronous cache sweep in AuthProvider below.
    const hasPersistedProviderRef = useRef(false)
    // The render-phase write below is deliberate - see the comment above. Moving it into an
    // effect reintroduces the race it exists to prevent, so the rule is suppressed here.
    // eslint-disable-next-line react-hooks/refs
    if (isAuthenticated && !hasPersistedProviderRef.current && !isKindeCacheActive()) {
        hasPersistedProviderRef.current = true
        setAuthProvider(AUTH_PROVIDERS.KINDE)
    }

    // Detect reverted/deleted Kinde user: SDK finishes loading but user is not
    // authenticated. Clear provider cache so the app falls through to B2C /
    // email-first login instead of auto-redirecting back to Kinde's login page.
    //
    // Skip if URL has login_hint (first-time Kinde login via email-first flow)
    // — in that case UserRequiredContainer will call login() to redirect to Kinde.
    //
    // QM-3353: Only fire for an actual cached Kinde provider. With the new
    // registration-provider fallback in AuthProvider, a fresh visitor (cache
    // null) can mount KindeProvider via fallback — without this guard, the
    // redirect-to-/ would loop forever because each reload re-enters fallback.
    //
    // QM-3393 Phase 2: the purge is synchronous. A 3s "grace window" was trialled
    // (Phase 1) on the hypothesis that a token-rotation race transiently flipped
    // `isAuthenticated` false; production telemetry falsified it — across 266 bounced
    // users, `bridge:auth-recovered` never fired once. `isInitializing` is the SDK's
    // `isLoading`, so by the time it's false the on-load refresh has already settled;
    // there is no later async transition to wait for. The window only delayed every
    // bounce by 3s, so it was removed. The hasCheckedSession ref keeps the purge to at
    // most once per mount.
    const hasCheckedSession = useRef(false)
    useEffect(() => {
        if (!isKindeCacheActive()) return
        if (isInitializing || isAuthenticated || hasCheckedSession.current) return
        hasCheckedSession.current = true

        const params = new URLSearchParams(window.location.search)
        if (params.has('login_hint') || params.has('code')) return

        // Don't clear cache or redirect on public routes — those pages are designed
        // to render without authentication (e.g., /shared/v2/... customer-facing PDFs).
        // Without this exemption, a Kinde-authenticated user opening a public doc in a
        // new tab would lose the session silently and be redirected to home (QM-3308).
        // Also exempts AUTH_RETURN_PATHS (e.g. the Zapier authorize page) — a
        // protected route that must render and start its own login rather than being
        // bounced to / first, because its URL carries an OAuth handshake that cannot be
        // rebuilt after a redirect.
        if (allowsUnauthenticatedEntry(window.location.pathname)) return

        console.warn('[Auth] Kinde session invalid after init — clearing provider cache')
        clearAuthProvider()
        window.location.href = '/'
    }, [isInitializing, isAuthenticated])

    const authValue = useMemo(
        () => ({
            provider: AUTH_PROVIDERS.KINDE,
            manager: kindeManager,
            // Computed properties for convenience
            isAuthenticated,
            isLoading: isInitializing,
            user,
        }),
        // More granular deps - only recompute when actual values change
        [isAuthenticated, isInitializing, user, kindeManager]
    )

    // Block children from rendering while the useEffect above is about to clear
    // the provider cache and redirect. Without this gate, UserRequiredContainer
    // would see the stale cached provider + !isAuthenticated and call login()
    // during render — racing ahead of the cleanup effect.
    // Placed after useMemo to satisfy React hooks ordering rules.
    //
    // QM-3353: Same `getAuthProvider() === KINDE` guard as the recovery effect
    // above — only block when there's an actual cached Kinde session being
    // recovered. For a fresh visitor (cache null) we want children to render
    // so UserRequiredContainer can call login() and redirect to Kinde.
    if (isKindeCacheActive() && !isInitializing && !isAuthenticated) {
        const params = new URLSearchParams(window.location.search)
        if (!params.has('login_hint') && !params.has('code') && !allowsUnauthenticatedEntry(window.location.pathname)) {
            return null
        }
    }

    return <AuthContext.Provider value={authValue}>{children}</AuthContext.Provider>
}

KindeAuthBridge.propTypes = {
    children: PropTypes.node.isRequired,
}

/**
 * Conditional authentication provider that wraps children with either
 * MsalProvider (Azure AD B2C) or KindeProvider based on user's auth type.
 *
 * Uses bridge components to ensure provider-specific hooks are only called
 * within their required SDK provider context.
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to wrap
 * @returns {JSX.Element} Wrapped children with appropriate auth provider
 *
 * @example
 * // In index.jsx:
 * <AuthProvider>
 *   <App />
 * </AuthProvider>
 */
export const AuthProvider = ({ children }) => {
    // QM-3353: Sweep stale B2C caches on environments that have moved to Kinde
    // (e.g. migrated users who haven't re-logged in since their migration).
    // Runs once per mount via a ref guard — the sweep MUST execute before the
    // provider-selection below, so this can't be moved to a useEffect (which
    // would fire after render and let MSAL mount on the first pass).
    // Self-extinguishing: once cleared, the condition is false on subsequent loads.
    //
    // Asymmetry note: the symmetric scenario (Kinde cache on a B2C-target env
    // like Libellula) is intentionally NOT swept here. It's theoretical today
    // (no shared localStorage across deployments) and adding the symmetric
    // branch would require widespread test updates for an edge case that
    // hasn't materialized. Revisit if Libellula starts seeing stale-Kinde
    // reports.
    /** @type {React.RefObject<boolean | null>} */
    const hasSweptCache = useRef(null)
    if (hasSweptCache.current === null) {
        hasSweptCache.current = true
        if (getAuthProvider() === AUTH_PROVIDERS.MSAL && getRegistrationProvider() === AUTH_PROVIDERS.KINDE) {
            clearAuthProvider()
        }
    }

    // Fall back to the registration provider when no provider is cached, so
    // brand-new users hit the right SDK on environments past the migration.
    // On Libellula (registration provider = B2C) this preserves MSAL as default.
    const provider = getAuthProvider() ?? getRegistrationProvider()

    if (provider === AUTH_PROVIDERS.KINDE) {
        return (
            <KindeProvider
                {...kindeConfig}
                callbacks={kindeCallbacks}
                refreshOnFocus
            >
                <KindeAuthBridge>{children}</KindeAuthBridge>
            </KindeProvider>
        )
    }

    // Default: MSAL (B2C) - lazy loaded for code-splitting
    return (
        <Suspense fallback={<TbxLoadingOverlay />}>
            <LazyMsalProviderWrapper>{children}</LazyMsalProviderWrapper>
        </Suspense>
    )
}

AuthProvider.propTypes = {
    children: PropTypes.node.isRequired,
}
